import { useState, useEffect, useRef, useCallback } from 'react';

interface UseAutoSaveOptions {
  delay?: number;
  enabled?: boolean;
  onSave?: (data: any) => Promise<void>;
  onError?: (error: Error) => void;
}

interface UseAutoSaveReturn {
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  lastSaved: Date | null;
  error: Error | null;
  save: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook für automatisches Speichern von Formulardaten
 * Speichert Daten nach einer Verzögerung, wenn sich der Wert geändert hat
 */
export const useAutoSave = <T>(
  value: T,
  options: UseAutoSaveOptions = {}
): UseAutoSaveReturn => {
  const {
    delay = 1000,
    enabled = true,
    onSave,
    onError
  } = options;

  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);
  
  const previousValue = useRef<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Speicher-Funktion
  const save = useCallback(async () => {
    if (!onSave || !enabled) return;

    try {
      setIsSaving(true);
      setError(null);
      
      await onSave(value);
      
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      previousValue.current = value;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unbekannter Fehler');
      setError(error);
      onError?.(error);
    } finally {
      setIsSaving(false);
    }
  }, [value, onSave, enabled, onError]);

  // Reset-Funktion
  const reset = useCallback(() => {
    setHasUnsavedChanges(false);
    setError(null);
    previousValue.current = value;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [value]);

  // Auto-Save-Logik
  useEffect(() => {
    if (!enabled || !onSave) return;

    // Prüfe ob sich der Wert geändert hat
    const hasChanged = JSON.stringify(value) !== JSON.stringify(previousValue.current);
    
    if (hasChanged) {
      setHasUnsavedChanges(true);
      
      // Vorherigen Timeout löschen
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Neuen Timeout setzen
      timeoutRef.current = setTimeout(() => {
        save();
      }, delay);
    }

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay, enabled, onSave, save]);

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    save,
    reset
  };
};

export default useAutoSave;
